import React from 'react';
import dayjs from 'dayjs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { TableCell, TableRow } from 'semantic-ui-react';

import { ROUTES } from 'Constants';
import { CharterScheduleItem, CharterStatus } from 'Modules/Charter/Types/CharterScheduleItem';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import { charterPaymentLabel, charterPaymentTagStyles } from 'Modules/Charter/Utils/charterPaymentLabel';
import { CharterColumnKeys } from './CharterColumnKeys';

export interface CharterRenderProps {
	charter: CharterScheduleItem;
	/** Which columns the reader has ticked. A key that is absent is not drawn. */
	visibleColumns: Set<string>;
	/**
	 * The reader is the customer rather than staff, so the row opens their own view of the charter.
	 * The staff sidebar lives over the staff schedule, which a customer cannot reach at all.
	 */
	viewerIsCustomer?: boolean;
}

/** One date and time, written the way the charter itself writes them. */
const dateTime = (value: Date) => `${dayjs(value).format('DD MMM YYYY')}, ${formatStandardTime(value)}`;

/** Where a charter is up to, in the colours the rest of the staff tables use for the same three ideas. */
const STATUS_TAG_CLASSES: Record<CharterStatus, string> = {
	Reserved: 'tag--warning',
	Booked: 'tag--success',
	Cancelled: 'tag--error',
};

/**
 * A charter as one row of the charters table.
 *
 * The row opens the way a bookings-table row does: in this tab on a plain click, in a new one on a
 * middle-click or with Ctrl / Cmd held. It used to open a new tab every time, because the charter's
 * sidebar closed back to the staff schedule and the reader would have lost their place in this table.
 * The table's own address now rides along in the navigation, so closing the sidebar comes back here —
 * and the filters and page survive the trip, since the staff schedule is inside the charters table's
 * section (see STAFF_TABLE_SECTIONS.CHARTERS).
 *
 * The customer cell is the exception — it opens that customer's page, so it stops the row's click
 * from also firing. Without that, clicking the customer would open the charter as well.
 */
export function CharterRender({ charter, visibleColumns, viewerIsCustomer }: CharterRenderProps) {
	const charterUrl = viewerIsCustomer
		? ROUTES.CUSTOMER_CHARTER.url(charter.id)
		: ROUTES.STAFF_SCHEDULE_CHARTER.url(charter.id);

	const navigate = useNavigate();
	const location = useLocation();

	// Staff land on the charter's sidebar, which closes to wherever `returnTo` says. A customer lands on
	// their own charter view, whose back button already knows where their list is (charterBackUrl).
	const openHere = () =>
		navigate(
			charterUrl,
			viewerIsCustomer ? undefined : { state: { returnTo: `${location.pathname}${location.search}` } },
		);

	const openElsewhere = () => window.open(charterUrl, '_blank');

	// The bookings table's rule, so the two rows behave alike.
	const handleClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
		if (e.button === 1 || e.ctrlKey || e.metaKey) {
			openElsewhere();
			return;
		}

		openHere();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			openHere();
		}
	};

	return (
		<TableRow
			onClick={handleClick}
			// Middle-click, which does not fire onClick.
			onAuxClick={handleClick}
			onKeyDown={handleKeyDown}
			className="clickable-row"
			style={{ cursor: 'pointer' }}
			tabIndex={0}
			data-test-id={`charter-row-${charter.id}`}
		>
			{/*
			  * The reference leads the row: it is the only thing on a charter that is always there and
			  * always unique, which is what a reader needs to tell one row from the next.
			  */}
			{visibleColumns.has(CharterColumnKeys.REFERENCE) && (
				<TableCell>{charter.humanReadableId || '-'}</TableCell>
			)}

			{/* A charter can be pencilled in before anyone has said what it is for. */}
			{visibleColumns.has(CharterColumnKeys.NAME) && (
				<TableCell>{charter.name?.trim() || 'Charter'}</TableCell>
			)}

			{visibleColumns.has(CharterColumnKeys.CUSTOMER) && (
				// The one cell that is not the row: it opens the customer, so it has to stop the row's
				// click reaching the handler that opens the charter.
				<TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>
					{charter.customer ? (
						<Link
							to={ROUTES.USER_DETAIL.url(charter.customer.id, 'bookings')}
							className="customer-details hide-underline"
							style={{ color: 'inherit' }}
						>
							<div className="customer-details__inner">
								<p>{`${charter.customer.firstName ?? ''} ${charter.customer.lastName ?? ''}`.trim() || '-'}</p>
								<p>{charter.customer.email || ''}</p>
							</div>
						</Link>
					) : (
						// A reserve is routinely taken before anybody has said whose it is, so this is
						// the normal state of a held ferry rather than missing data.
						<span className="grey">Unassigned</span>
					)}
				</TableCell>
			)}

			{visibleColumns.has(CharterColumnKeys.FERRY) && <TableCell>{charter.ferry?.name || '-'}</TableCell>}

			{visibleColumns.has(CharterColumnKeys.START_DATE) && (
				<TableCell>{dateTime(charter.startDateTime)}</TableCell>
			)}

			{visibleColumns.has(CharterColumnKeys.END_DATE) && (
				<TableCell>{dateTime(charter.endDateTime)}</TableCell>
			)}

			{visibleColumns.has(CharterColumnKeys.PRICE) && (
				<TableCell>{charter.price != null ? formatPriceDisplay(charter.price, false) : '-'}</TableCell>
			)}

			{visibleColumns.has(CharterColumnKeys.STATUS) && (
				<TableCell>
					<Tag text={charter.status} tagClassName={STATUS_TAG_CLASSES[charter.status]} />
				</TableCell>
			)}

			{/* Empty on a reserved charter, which owes nothing and so has no payment to be up to. */}
			{visibleColumns.has(CharterColumnKeys.PAYMENT) && (
				<TableCell>
					{charter.payment && (
						<Tag
							text={charterPaymentLabel(charter.payment.state, charter.isInvoiced)}
							customStyles={charterPaymentTagStyles(charter.payment.state, charter.isInvoiced)}
						/>
					)}
				</TableCell>
			)}

			{visibleColumns.has(CharterColumnKeys.INVOICE_NUMBER) && (
				<TableCell>{charter.payment?.invoiceNumber || '-'}</TableCell>
			)}
		</TableRow>
	);
}
